import React, { useEffect, useState, useCallback } from "react";
import Sidebar from "./Sidebar";
import LoadingSpinner from "./LoadingSpinner";
import { useNavigate } from "react-router-dom";
import "./dashboard.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSignOutAlt,
  faUsers,
  faChalkboardTeacher,
  faGraduationCap,
  faBook,
  faChartLine,
  faCalendarAlt,
  faBell,
  faCog,
  faUserShield,
  faDatabase,
  faShieldAlt,
  faRocket,
  faChartBar,
  faUserGraduate,
  faSchool,
  faClipboardList,
  faMedal,
  faExclamationTriangle,
  faCheckCircle,
  faSyncAlt,
  faHistory,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

// const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api"; // Your PHP backend
const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api"; // Your PHP backend

// const AUTH_BASE_URL = "https://mhsdev.online/MHS-WEB/api/auth.php";
const AUTH_BASE_URL = "https://mhsdev.online/MHS-WEB/api/auth.php";

const CACHE_KEY = "dashboard_cache";
const CACHE_DURATION = 60 * 60 * 1000;

const Dashboard = () => {
  const [showSlider, setShowSlider] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [cachedData, setCachedData] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
const navigate = useNavigate();

  const [dashboardStats, setDashboardStats] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [recentActivities, setRecentActivities] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [topStudents, setTopStudents] = useState([]);
  const [classStats, setClassStats] = useState([]);
  
  const [targetStats, setTargetStats] = useState({
    totalStudents: 0,
    totalTeachers: 0,
    activeClasses: 0,
    avgAttendance: 0,
    pendingTasks: 0,
    recentActivity: 0,
  });

  const [displayStats, setDisplayStats] = useState({
    totalStudents: 0,
    totalTeachers: 0,
    activeClasses: 0,
    avgAttendance: "0%",
    pendingTasks: 0,
    recentActivity: 0,
  });

  const loadFromCache = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { timestamp, data } = JSON.parse(cached);
        const now = Date.now();
        
        if (now - timestamp < CACHE_DURATION) {
          console.log("Using cached data");
          return { data, timestamp };
        } else {
          console.log("Cache expired");
          localStorage.removeItem(CACHE_KEY);
        }
      }
    } catch (error) {
      console.error("Error loading cache:", error);
    }
    return null;
  };

  const saveToCache = (data) => {
    try {
      const cacheData = {
        timestamp: Date.now(),
        data: data
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
      setLastRefreshTime(new Date());
      console.log("Data saved to cache");
    } catch (error) {
      console.error("Error saving cache:", error);
    }
  };

  const applyCachedData = (cacheData) => {
    if (!cacheData) return false;
    
    const {
      dashboardStats: cachedStats,
      attendanceData: cachedAttendance,
      performanceMetrics: cachedPerformance,
      recentActivities: cachedActivities,
      systemHealth: cachedHealth,
      topStudents: cachedTopStudents,
      classStats: cachedClassStats
    } = cacheData;
    
    if (cachedStats?.success) {
      setDashboardStats(cachedStats);
      setTargetStats({
        totalStudents: cachedStats.stats?.overview?.total_students || 0,
        totalTeachers: cachedStats.stats?.overview?.total_teachers || 0,
        activeClasses: cachedStats.stats?.overview?.total_classes || 0,
        avgAttendance: cachedStats.stats?.overview?.attendance_rate || 0,
        pendingTasks: cachedStats.stats?.recent_activities?.count || 0,
        recentActivity: cachedStats.stats?.recent_activities?.count || 0,
      });
      
      setDisplayStats({
        totalStudents: cachedStats.stats?.overview?.total_students || 0,
        totalTeachers: cachedStats.stats?.overview?.total_teachers || 0,
        activeClasses: cachedStats.stats?.overview?.total_classes || 0,
        avgAttendance: `${cachedStats.stats?.overview?.attendance_rate || 0}%`,
        pendingTasks: cachedStats.stats?.recent_activities?.count || 0,
        recentActivity: cachedStats.stats?.recent_activities?.count || 0,
      });
    }
    
    if (cachedAttendance?.success) setAttendanceData(cachedAttendance);
    if (cachedPerformance?.success) setPerformanceMetrics(cachedPerformance);
    if (cachedActivities?.success) setRecentActivities(cachedActivities);
    if (cachedHealth?.success) setSystemHealth(cachedHealth);
    if (cachedTopStudents?.success) setTopStudents(cachedTopStudents.top_students_overall || []);
    if (cachedClassStats?.success) setClassStats(cachedClassStats.class_stats || []);
    
    return true;
  };

  const fetchDashboardData = useCallback(async (forceRefresh = false) => {
    if (isRefreshing && !forceRefresh) return;
    
    setIsRefreshing(true);
    
    if (!forceRefresh) {
      const cache = loadFromCache();
      if (cache) {
        applyCachedData(cache.data);
        setCachedData(cache.data);
        setDataLoaded(true);
        setIsRefreshing(false);
        setLastRefreshTime(new Date(cache.timestamp));
        
        setTimeout(() => {
          fetchDashboardData(true);
        }, 1000);
        return true;
      }
    }
    
    try {
      setLoadingProgress(30);
      
      const endpoints = [
        `${API_BASE_URL}/dashboard.php?action=getDashboardStats`,
        `${API_BASE_URL}/dashboard.php?action=getTodayAttendance`,
        `${API_BASE_URL}/dashboard.php?action=getPerformanceMetrics`,
        `${API_BASE_URL}/dashboard.php?action=getRecentActivities&limit=10`,
        `${API_BASE_URL}/dashboard.php?action=getSystemHealth`,
        `${API_BASE_URL}/dashboard.php?action=getTopPerformingStudents&limit=5`,
        `${API_BASE_URL}/dashboard.php?action=getClassWiseStats`,
      ];

      const responses = await Promise.all(
        endpoints.map(url => fetch(url).then(res => res.json()))
      );

      const processedData = {};
      
      setLoadingProgress(60);
      
      if (responses[0]?.success) {
        const stats = responses[0];
        setDashboardStats(stats);
        processedData.dashboardStats = stats;
        
        setTargetStats({
          totalStudents: stats.stats?.overview?.total_students || 0,
          totalTeachers: stats.stats?.overview?.total_teachers || 0,
          activeClasses: stats.stats?.overview?.total_classes || 0,
          avgAttendance: stats.stats?.overview?.attendance_rate || 0,
          pendingTasks: stats.stats?.recent_activities?.count || 0,
          recentActivity: stats.stats?.recent_activities?.count || 0,
        });
        
        setDisplayStats({
          totalStudents: stats.stats?.overview?.total_students || 0,
          totalTeachers: stats.stats?.overview?.total_teachers || 0,
          activeClasses: stats.stats?.overview?.total_classes || 0,
          avgAttendance: `${stats.stats?.overview?.attendance_rate || 0}%`,
          pendingTasks: stats.stats?.recent_activities?.count || 0,
          recentActivity: stats.stats?.recent_activities?.count || 0,
        });
      }

      if (responses[1]?.success) {
        setAttendanceData(responses[1]);
        processedData.attendanceData = responses[1];
      }

      if (responses[2]?.success) {
        setPerformanceMetrics(responses[2]);
        processedData.performanceMetrics = responses[2];
      }

      if (responses[3]?.success) {
        setRecentActivities(responses[3]);
        processedData.recentActivities = responses[3];
      }

      if (responses[4]?.success) {
        setSystemHealth(responses[4]);
        processedData.systemHealth = responses[4];
      }

      if (responses[5]?.success) {
        setTopStudents(responses[5].top_students_overall || []);
        processedData.topStudents = responses[5];
      }

      if (responses[6]?.success) {
        setClassStats(responses[6].class_stats || []);
        processedData.classStats = responses[6];
      }

      saveToCache(processedData);
      setCachedData(processedData);
      
      setLoadingProgress(100);
      setDataLoaded(true);
      setIsRefreshing(false);
      return true;

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setDataLoaded(true);
      setIsRefreshing(false);
      setLoadingProgress(100);
      
      const cache = loadFromCache();
      if (cache) {
        applyCachedData(cache.data);
        setCachedData(cache.data);
      }
      
      return false;
    }
  }, [isRefreshing]);

  const handleManualRefresh = () => {
    fetchDashboardData(true);
  };

  useEffect(() => {
    if (!dataLoaded || cachedData) return;

    const interval = setInterval(() => {
      setDisplayStats((prev) => {
        const newStats = { ...prev };
        let allTargetsReached = true;

        if (prev.totalStudents < targetStats.totalStudents) {
          const step = Math.ceil(targetStats.totalStudents / 50);
          newStats.totalStudents = Math.min(prev.totalStudents + step, targetStats.totalStudents);
          allTargetsReached = false;
        }

        if (prev.totalTeachers < targetStats.totalTeachers) {
          const step = Math.ceil(targetStats.totalTeachers / 30);
          newStats.totalTeachers = Math.min(prev.totalTeachers + step, targetStats.totalTeachers);
          allTargetsReached = false;
        }

        if (prev.activeClasses < targetStats.activeClasses) {
          const step = Math.ceil(targetStats.activeClasses / 20);
          newStats.activeClasses = Math.min(prev.activeClasses + step, targetStats.activeClasses);
          allTargetsReached = false;
        }

        const currentAttendance = parseFloat(prev.avgAttendance.replace('%', ''));
        if (currentAttendance < targetStats.avgAttendance) {
          const step = Math.ceil((targetStats.avgAttendance - currentAttendance) / 20);
          const newAttendance = Math.min(currentAttendance + step, targetStats.avgAttendance);
          newStats.avgAttendance = `${newAttendance.toFixed(1)}%`;
          allTargetsReached = false;
        }

        if (prev.pendingTasks < targetStats.pendingTasks) {
          const step = Math.ceil(targetStats.pendingTasks / 15);
          newStats.pendingTasks = Math.min(prev.pendingTasks + step, targetStats.pendingTasks);
          allTargetsReached = false;
        }

        if (prev.recentActivity < targetStats.recentActivity) {
          const step = Math.ceil(targetStats.recentActivity / 25);
          newStats.recentActivity = Math.min(prev.recentActivity + step, targetStats.recentActivity);
          allTargetsReached = false;
        }

        if (allTargetsReached) {
          clearInterval(interval);
        }

        return newStats;
      });
    }, 50);

    return () => clearInterval(interval);
  }, [dataLoaded, targetStats, cachedData]);

  useEffect(() => {
    setShowSlider(true);
    
    const cache = loadFromCache();
    if (cache) {
      console.log("Found cached data, showing immediately");
      applyCachedData(cache.data);
      setCachedData(cache.data);
      setDataLoaded(true);
      setLastRefreshTime(new Date(cache.timestamp));
      setIsLoading(false);
      
      setTimeout(() => {
        fetchDashboardData(true);
      }, 500);
    } else {
      console.log("No cache found, fetching fresh data");
      setIsLoading(true);
      setLoadingProgress(10);
      fetchDashboardData(true).then(() => {
        setLoadingProgress(100);
        setTimeout(() => {
          setIsLoading(false);
        }, 300);
      });
    }

    const timeout = setTimeout(() => {
      if (isLoading) {
        console.warn("Loading timeout - proceeding with available content");
        setDataLoaded(true);
        setIsLoading(false);
        setLoadingProgress(100);
      }
    }, 8000);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const handleImageError = (e, fallbackSrc) => {
    e.target.onerror = null;
    e.target.src = fallbackSrc;
  };

 const handleLogout = async () => {
  setIsLoggingOut(true);
  
  try {
    const token = localStorage.getItem('auth_token') || '';
    console.log('Token being sent:', token);
    console.log('API URL:', `${AUTH_BASE_URL}?action=logout`);
    
    // Try with simple GET request first
    const response = await fetch(`${AUTH_BASE_URL}?action=logout`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    const data = await response.json();
    console.log('Backend response:', data);
    
    if (data.success) {
      localStorage.clear();
      sessionStorage.clear();
      localStorage.removeItem(CACHE_KEY);
      
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    } else {
      console.error('Logout failed:', data.message);
      // Force logout locally even if API fails
      localStorage.clear();
      sessionStorage.clear();
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    }
  } catch (error) {
    console.error('Logout network error:', error);
    localStorage.clear();
    sessionStorage.clear();
    setTimeout(() => {
      window.location.href = '/';
    }, 1000);
  }
};

const handleCardClick = (action) => {
  if (action.title === "Logout") {
    setShowLogoutModal(true);
  } else if (action.link && action.link !== "#") {
    navigate(action.link);
  } else {
    console.log(`Click on ${action.title}`);
  }
};

  const quickActions = [
  {
    id: 1,
    title: "Manage Students",
    icon: "/images/icons/manage-std.webp",
    color: "card-students",
    description: "Manage student records and enrollments",
    count: displayStats.totalStudents,
    badge: "Active",
    fallback: "https://cdn-icons-png.flaticon.com/512/3135/3135715.png",
    link: "/manage-students"  // Matches sidebar route
  },
  {
    id: 2,
    title: "Manage Teachers",
    icon: "/images/icons/manage-thr.webp",
    color: "card-teachers",
    description: "Handle teacher profiles and assignments",
    count: displayStats.totalTeachers,
    badge: "Staff",
    fallback: "https://cdn-icons-png.flaticon.com/512/3135/3135715.png",
    link: "/manage-Teachers"  // Matches sidebar route
  },
  {
    id: 3,
    title: "Manage Classes",
    icon: "/images/icons/class.png",
    color: "card-classes",
    description: "Organize class schedules and sections",
    count: displayStats.activeClasses,
    badge: "Active",
    fallback: "https://cdn-icons-png.flaticon.com/512/3135/3135715.png",
    link: "/manageSections"  // Matches route for managing sections
  },
  {
    id: 4,
    title: "Manage Subjects",
    icon: "/images/icons/books.png",
    color: "card-subjects",
    description: "Curriculum and subject management",
    count: systemHealth?.table_stats?.['assigned_subjects']?.row_count || 28,
    badge: "Catalog",
    fallback: "https://cdn-icons-png.flaticon.com/512/3135/3135715.png",
    link: "/Subject Management"  // Matches sidebar route
  },
  {
    id: 5,
    title: "Manage Results",
    icon: "/images/icons/results.png",
    color: "card-results",
    description: "Examination results and analytics",
    count: performanceMetrics?.grade_distribution?.[0]?.grade || "A+",
    badge: "Reports",
    fallback: "https://cdn-icons-png.flaticon.com/512/3135/3135715.png",
    link: "/Result Management Dashboard"  // Matches sidebar route
  },
{
    id: 5,
    title: "Manage Inventories",
    icon: "/images/icons/manage.webp",
    color: "card-inventory",
    description: "Track stock levels, supplies, and equipment",
    // count: inventoryMetrics?.total_items || "1,250",
    count:  "1,250",

    badge: "Stock",
    fallback: "https://cdn-icons-png.flaticon.com/512/2897/2897785.png", // Box/Inventory icon
    link: "/Inventory Management Dashboard"  // Make sure this matches your router
  },
  {
    id: 6,
    title: "Logout",
    icon: faSignOutAlt,
    color: "card-logout",
    description: "Secure system logout",
    badge: "Security",
    link: "#"  // Special case - triggers modal instead of navigation
  },
];

  const systemOverview = [
    {
      label: "Total Students",
      value: displayStats.totalStudents,
      icon: faUsers,
      color: "#4CAF50",
    },
    {
      label: "Active Teachers",
      value: displayStats.totalTeachers,
      icon: faChalkboardTeacher,
      color: "#2196F3",
    },
    {
      label: "Classes Running",
      value: displayStats.activeClasses,
      icon: faGraduationCap,
      color: "#9C27B0",
    },
    {
      label: "Avg Attendance",
      value: displayStats.avgAttendance,
      icon: faCalendarAlt,
      color: "#FF9800",
    },
    { 
      label: "Pending Tasks", 
      value: displayStats.pendingTasks, 
      icon: faBell,
      color: "#F44336",
    },
    {
      label: "Today's Activity",
      value: displayStats.recentActivity,
      icon: faChartLine,
      color: "#00BCD4",
    },
  ];

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return "Just now";
    
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return past.toLocaleDateString();
  };

  const getLargestClass = () => {
    if (!classStats || classStats.length === 0) return "N/A";
    const largest = classStats.reduce((max, cls) => 
      cls.student_count > max.student_count ? cls : max
    );
    return `${largest.class} (${largest.student_count} students)`;
  };

  const getTopStudent = () => {
    if (!topStudents || topStudents.length === 0) return "No data";
    const top = topStudents[0];
    return `${top.student_name} - ${top.avg_percentage}% (${top.grade})`;
  };

  const formatLastRefresh = () => {
    if (!lastRefreshTime) return "Never";
    const now = new Date();
    const diffMs = now - lastRefreshTime;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
    return lastRefreshTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {isLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'rgba(255, 255, 255, 0.95)',
          zIndex: 9999,
          backdropFilter: 'blur(5px)'
        }}>
          <LoadingSpinner
            message="Loading your dashboard..."
            subMessage={cachedData ? "Using cached data for faster loading..." : "Fetching fresh data..."}
          />
          <div style={{
            width: '200px',
            height: '4px',
            background: '#e0e0e0',
            borderRadius: '2px',
            marginTop: '20px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              background: 'linear-gradient(90deg, #2E2A74, #4CAF50)',
              width: `${loadingProgress}%`,
              transition: 'width 0.3s ease'
            }} />
          </div>
          <div style={{
            marginTop: '15px',
            color: '#2E2A74',
            fontSize: '1rem',
            textAlign: 'center',
            maxWidth: '400px'
          }}>
            {loadingProgress < 30 ? "Initializing..." : 
             loadingProgress < 60 ? "Loading system data..." : 
             loadingProgress < 90 ? "Rendering dashboard..." : 
             "Almost ready..."}
          </div>
        </div>
      )}

      {showLogoutModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 10000,
          backdropFilter: 'blur(10px)',
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '30px',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            transform: isLoggingOut ? 'scale(0.95)' : 'scale(1)',
            transition: 'transform 0.3s ease',
            animation: 'slideUp 0.5s ease-out'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{
                margin: 0,
                color: '#2E2A74',
                fontSize: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <FontAwesomeIcon icon={faSignOutAlt} />
                Confirm Logout
              </h2>
              <button
                onClick={() => !isLoggingOut && setShowLogoutModal(false)}
                disabled={isLoggingOut}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.2rem',
                  cursor: isLoggingOut ? 'not-allowed' : 'pointer',
                  color: '#666',
                  padding: '5px',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {isLoggingOut ? (
              <div style={{
                textAlign: 'center',
                padding: '30px 0'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  border: '3px solid #f3f3f3',
                  borderTop: '3px solid #2E2A74',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 20px'
                }}></div>
                <p style={{
                  color: '#2E2A74',
                  fontSize: '1.1rem',
                  fontWeight: '500'
                }}>
                  Logging out...
                </p>
                <p style={{
                  color: '#666',
                  fontSize: '0.9rem',
                  marginTop: '10px'
                }}>
                  Securing your session and redirecting
                </p>
              </div>
            ) : (
              <>
                <div style={{
                  background: 'linear-gradient(135deg, #ff6b6b, #ee5a52)',
                  borderRadius: '15px',
                  padding: '20px',
                  marginBottom: '25px',
                  textAlign: 'center',
                  color: 'white'
                }}>
                  <div style={{
                    fontSize: '3rem',
                    marginBottom: '10px',
                    animation: 'pulse 2s infinite'
                  }}>
                    ⚠️
                  </div>
                  <h3 style={{
                    margin: '0 0 10px 0',
                    fontSize: '1.2rem'
                  }}>
                    Security Alert
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: '0.9rem',
                    opacity: 0.9
                  }}>
                    You are about to log out from the system
                  </p>
                </div>

                <div style={{
                  background: '#f8f9fa',
                  borderRadius: '10px',
                  padding: '15px',
                  marginBottom: '25px'
                }}>
                  <p style={{
                    margin: 0,
                    color: '#666',
                    fontSize: '0.95rem',
                    lineHeight: 1.5
                  }}>
                    <strong>Note:</strong> This action will:
                  </p>
                  <ul style={{
                    margin: '10px 0 0 0',
                    paddingLeft: '20px',
                    color: '#666',
                    fontSize: '0.9rem',
                    lineHeight: 1.6
                  }}>
                    <li>End your current session</li>
                    <li>Clear all cached data</li>
                    <li>Require re-authentication to access</li>
                    <li>Redirect to login page</li>
                  </ul>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '15px',
                  justifyContent: 'center'
                }}>
                  <button
                    onClick={() => setShowLogoutModal(false)}
                    style={{
                      padding: '12px 25px',
                      background: '#f0f0f0',
                      color: '#666',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      fontWeight: '500',
                      transition: 'all 0.3s',
                      flex: 1
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#e0e0e0'}
                    onMouseLeave={(e) => e.target.style.background = '#f0f0f0'}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleLogout}
                    style={{
                      padding: '12px 25px',
                      background: 'linear-gradient(135deg, #ff6b6b, #ee5a52)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      fontWeight: '500',
                      transition: 'all 0.3s',
                      flex: 1,
                      boxShadow: '0 4px 15px rgba(238, 90, 82, 0.3)'
                    }}
                    onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                  >
                    Confirm Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div
        className="new-n-dashboard-wrapper"
        style={{ 
          opacity: isLoading ? 0 : 1, 
          transition: "opacity 0.3s",
          display: isLoading ? 'none' : 'flex'
        }}
      >
        <Sidebar />

        <div className="main-content">
          <div className="hero-banner fade-in-up" style={{ marginTop: "-3vh" }}>
            <img
              src="/images/mhs bnner.png"
              alt="MHS Education System"
              loading="lazy"
              onError={(e) =>
                handleImageError(
                  e,
                  "https://via.placeholder.com/1200x280/2E2A74/FFFFFF?text=DEA+Education+System"
                )
              }
            />
          </div>

          <div className={`status-banner ${showSlider ? "visible" : "hidden"}`}>
            <div className="banner-content">
              <div className="banner-text">
                <p
                  style={{
                    fontWeight: 600,
                    fontSize: "1.2rem",
                    marginBottom: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <span>🎓</span>
                  <span>Welcome to MHS School Management System</span>
                  {cachedData && (
                    <span style={{
                      fontSize: "0.8rem",
                      background: "#4CAF50",
                      color: "white",
                      padding: "2px 8px",
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px"
                    }}>
                      <FontAwesomeIcon icon={faHistory} size="xs" />
                      Cached Data
                    </span>
                  )}
                  {isRefreshing && (
                    <span style={{
                      fontSize: "0.8rem",
                      background: "#2196F3",
                      color: "white",
                      padding: "2px 8px",
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px"
                    }}>
                      <FontAwesomeIcon icon={faSyncAlt} spin size="xs" />
                      Refreshing...
                    </span>
                  )}
                </p>
                <p style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span>
                    Real-time management system with {displayStats.totalStudents} active students. 
                    Last updated: {dashboardStats?.current_status?.time || "Today"}
                  </span>
                  {lastRefreshTime && (
                    <span style={{
                      fontSize: "0.8rem",
                      background: "#f0f0f0",
                      color: "#666",
                      padding: "2px 8px",
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      gap: "5px"
                    }}>
                      <FontAwesomeIcon icon={faSyncAlt} size="xs" />
                      Updated {formatLastRefresh()}
                    </span>
                  )}
                </p>
              </div>
              <div className="banner-stats">
                <div className="dash-stat-badge">
                  <div className="dash-stat-number">
                    {displayStats.totalStudents}
                    <small style={{ fontSize: "0.7rem", opacity: 0.8 }}>
                      students
                    </small>
                  </div>
                  <div className="dash-stat-label">Active</div>
                </div>
                <div className="dash-stat-badge">
                  <div className="dash-stat-number">
                    {displayStats.totalTeachers}
                    <small style={{ fontSize: "0.7rem", opacity: 0.8 }}>
                      staff
                    </small>
                  </div>
                  <div className="dash-stat-label">Teachers</div>
                </div>
                {attendanceData && (
                  <div className="dash-stat-badge">
                    <div className="dash-stat-number" style={{ color: displayStats.avgAttendance > 90 ? "#4CAF50" : "#FF9800" }}>
                      {displayStats.avgAttendance}
                    </div>
                    <div className="dash-stat-label">Attendance</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <section className="dash-quick-actions">
            <div className="new-section-header">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                <div>
                  <h2 className="section-title">Quick Actions</h2>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      fontSize: "0.875rem",
                      color: "var(--text-secondary)",
                      marginTop: "5px",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faRocket}
                      style={{ color: "var(--primary-yellow)" }}
                    />
                    <span>Click any card to get started</span>
                  </div>
                </div>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "15px",
                }}>
                  {systemHealth && (
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      fontSize: "0.8rem",
                      background: "#f5f5f5",
                      padding: "8px 15px",
                      borderRadius: "20px",
                    }}>
                      <FontAwesomeIcon 
                        icon={isRefreshing ? faSyncAlt : faDatabase} 
                        spin={isRefreshing}
                        style={{ color: isRefreshing ? "#2196F3" : "#4CAF50" }} 
                      />
                      <span>Records: <strong>{systemHealth.table_stats?.['2k24_students']?.row_count || 0}</strong></span>
                    </div>
                  )}
                  <button
                    onClick={handleManualRefresh}
                    disabled={isRefreshing}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px 16px",
                      background: isRefreshing ? "#f0f0f0" : "var(--primary-indigo)",
                      color: isRefreshing ? "#666" : "white",
                      border: "none",
                      borderRadius: "20px",
                      cursor: isRefreshing ? "not-allowed" : "pointer",
                      fontSize: "0.875rem",
                      fontWeight: "500",
                      transition: "all 0.3s",
                    }}
                  >
                    <FontAwesomeIcon 
                      icon={faSyncAlt} 
                      spin={isRefreshing}
                    />
                    {isRefreshing ? "Refreshing..." : "Refresh Data"}
                  </button>
                </div>
              </div>
            </div>

            <div className="actions-grid">
              {quickActions.map((action, index) => (
                <div
                  key={action.id}
                  className={`action-card ${action.color}`}
                  onClick={() => handleCardClick(action)}
                  style={{ "--card-index": index }}
                  role="button"
                  tabIndex="0"
                  aria-label={`Open ${action.title}`}
                >
                  {action.badge && (
                    <div className="card-badge">{action.badge}</div>
                  )}

                  <div className="card-icon-container">
                    {typeof action.icon === "string" ? (
                      <img
                        src={action.icon}
                        alt={action.title}
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = action.fallback;
                        }}
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={action.icon}
                        style={{ fontSize: "2rem", color: "white" }}
                      />
                    )}
                  </div>

                  <h3>{action.title}</h3>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "var(--text-secondary)",
                      marginTop: "8px",
                      lineHeight: "1.4",
                    }}
                  >
                    {action.description}
                  </p>
                  
                  {action.count && (
                  <div className="colorful-animated-count">
                    {action.count}
                  </div>
                )}
                </div>
              ))}
            </div>
          </section>

          <div className="system-overview">
            <div className="overview-header">
              <h3 className="overview-title">
                <FontAwesomeIcon icon={faChartLine} />
                System Overview - {cachedData ? "Cached" : "Live"} Data
              </h3>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "0.875rem",
                  color: "var(--primary-indigo)",
                  background: "rgba(46, 42, 116, 0.1)",
                  padding: "8px 16px",
                  borderRadius: "20px",
                }}
              >
                <FontAwesomeIcon icon={faShieldAlt} />
                Cache:{" "}
                <strong style={{ color: cachedData ? "#4CAF50" : "#2196F3" }}>
                  {cachedData ? 'Active' : 'Fresh'}
                </strong>
              </div>
            </div>

            <div className="overview-grid">
              {systemOverview.map((item, index) => (
                <div key={index} className="overview-item" style={{ borderLeft: `4px solid ${item.color}` }}>
                  <div className="overview-value" style={{ color: item.color }}>
                    {item.value}
                  </div>
                  <div className="overview-label">{item.label}</div>
                  <div
                    style={{
                      marginTop: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: "var(--primary-indigo)",
                      fontSize: "0.875rem",
                      cursor: "pointer",
                    }}
                    onClick={() => console.log(`View ${item.label} details`)}
                  >
                    <FontAwesomeIcon icon={item.icon} />
                    <span>View Details</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "24px",
            marginBottom: "48px",
          }}>
            <div
              style={{
                background: "var(--card-bg)",
                borderRadius: "var(--radius-xl)",
                padding: "24px",
                border: "2px solid var(--border-color)",
                boxShadow: "var(--shadow-md)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    background: "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </div>
                <h3 style={{ color: "var(--primary-indigo)", margin: 0 }}>
                  Today's Attendance
                </h3>
              </div>
              
              {attendanceData ? (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                    <div>
                      <div style={{ fontSize: "2rem", fontWeight: "bold", color: "#4CAF50" }}>
                        {attendanceData.overall_attendance?.percentage || 0}%
                      </div>
                      <div style={{ fontSize: "0.9rem", color: "var(--text-secondary)" }}>
                        Overall Attendance Rate
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: "1.2rem", fontWeight: "bold" }}>
                        {attendanceData.overall_attendance?.present || 0} / {attendanceData.overall_attendance?.total_students || 0}
                      </div>
                      <div style={{ fontSize: "0.9rem", color: "var(--text-secondary)" }}>
                        Present / Total
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ 
                    background: "#f5f5f5", 
                    borderRadius: "10px", 
                    padding: "10px",
                    marginTop: "15px" 
                  }}>
                    <div style={{ fontSize: "0.9rem", fontWeight: "600", marginBottom: "8px" }}>
                      Recent Absentees ({attendanceData.absent_count || 0})
                    </div>
                    {attendanceData.absent_students && attendanceData.absent_students.length > 0 ? (
                      attendanceData.absent_students.slice(0, 3).map((student, idx) => (
                        <div key={idx} style={{ 
                          display: "flex", 
                          justifyContent: "space-between",
                          padding: "5px 0",
                          borderBottom: idx < 2 ? "1px solid #e0e0e0" : "none"
                        }}>
                          <span>{student.name}</span>
                          <span style={{ color: "#F44336" }}>{student.class}</span>
                        </div>
                      ))
                    ) : (
                      <div style={{ color: "var(--text-secondary)", textAlign: "center", padding: "10px" }}>
                        No absentees today
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div style={{ color: "var(--text-secondary)", textAlign: "center", padding: "20px" }}>
                  Loading attendance data...
                </div>
              )}
            </div>

            <div
              style={{
                background: "var(--card-bg)",
                borderRadius: "var(--radius-xl)",
                padding: "24px",
                border: "2px solid var(--border-color)",
                boxShadow: "var(--shadow-md)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    background: "linear-gradient(135deg, #2196F3 0%, #0D47A1 100%)",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <FontAwesomeIcon icon={faMedal} />
                </div>
                <h3 style={{ color: "var(--primary-indigo)", margin: 0 }}>
                  Top Performers
                </h3>
              </div>
              
              {topStudents.length > 0 ? (
                <>
                  <div style={{ marginBottom: "15px" }}>
                    <div style={{ fontSize: "0.9rem", color: "var(--text-secondary)", marginBottom: "5px" }}>
                      Top Student of the Year
                    </div>
                    <div style={{ fontSize: "1.1rem", fontWeight: "bold", color: "#2196F3" }}>
                      {getTopStudent()}
                    </div>
                  </div>
                  
                  <div style={{ 
                    background: "#f5f5f5", 
                    borderRadius: "10px", 
                    padding: "10px",
                    marginTop: "15px" 
                  }}>
                    <div style={{ fontSize: "0.9rem", fontWeight: "600", marginBottom: "8px" }}>
                      Top 3 Students
                    </div>
                    {topStudents.slice(0, 3).map((student, idx) => (
                      <div key={idx} style={{ 
                        display: "flex", 
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "5px 0",
                        borderBottom: idx < 2 ? "1px solid #e0e0e0" : "none"
                      }}>
                        <div>
                          <div style={{ fontWeight: "500" }}>{student.student_name}</div>
                          <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>
                            {student.class} • {student.section}
                          </div>
                        </div>
                        <div style={{ 
                          background: idx === 0 ? "#FFD700" : idx === 1 ? "#C0C0C0" : "#CD7F32",
                          color: idx === 0 ? "#000" : "#fff",
                          padding: "3px 8px",
                          borderRadius: "10px",
                          fontSize: "0.8rem",
                          fontWeight: "bold"
                        }}>
                          {student.avg_percentage}%
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{ color: "var(--text-secondary)", textAlign: "center", padding: "20px" }}>
                  {performanceMetrics ? "No exam data available" : "Loading performance data..."}
                </div>
              )}
            </div>

            <div
              style={{
                background: "var(--card-bg)",
                borderRadius: "var(--radius-xl)",
                padding: "24px",
                border: "2px solid var(--border-color)",
                boxShadow: "var(--shadow-md)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    background: "linear-gradient(135deg, #9C27B0 0%, #6A1B9A 100%)",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <FontAwesomeIcon icon={faSchool} />
                </div>
                <h3 style={{ color: "var(--primary-indigo)", margin: 0 }}>
                  Class Distribution
                </h3>
              </div>
              
              {classStats.length > 0 ? (
                <>
                  <div style={{ marginBottom: "15px" }}>
                    <div style={{ fontSize: "0.9rem", color: "var(--text-secondary)", marginBottom: "5px" }}>
                      Largest Class
                    </div>
                    <div style={{ fontSize: "1.1rem", fontWeight: "bold", color: "#9C27B0" }}>
                      {getLargestClass()}
                    </div>
                  </div>
                  
                  <div style={{ marginTop: "20px" }}>
                    <div style={{ fontSize: "0.9rem", fontWeight: "600", marginBottom: "10px" }}>
                      Student Count by Class
                    </div>
                    {classStats.slice(0, 4).map((cls, idx) => (
                      <div key={idx} style={{ marginBottom: "10px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                          <span>{cls.class}</span>
                          <span style={{ fontWeight: "500" }}>{cls.student_count} students</span>
                        </div>
                        <div style={{
                          height: "8px",
                          background: "#e0e0e0",
                          borderRadius: "4px",
                          overflow: "hidden"
                        }}>
                          <div style={{
                            height: "100%",
                            width: `${(cls.student_count / Math.max(...classStats.map(c => c.student_count))) * 100}%`,
                            background: "#9C27B0",
                            borderRadius: "4px"
                          }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{ color: "var(--text-secondary)", textAlign: "center", padding: "20px" }}>
                  Loading class data...
                </div>
              )}
            </div>
          </div>

          {recentActivities && recentActivities.activities && recentActivities.activities.length > 0 && (
            <div style={{
              background: "var(--card-bg)",
              borderRadius: "var(--radius-xl)",
              padding: "24px",
              border: "2px solid var(--border-color)",
              boxShadow: "var(--shadow-md)",
              marginBottom: "48px",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <h3 style={{ color: "var(--primary-indigo)", margin: 0, display: "flex", alignItems: "center", gap: "10px" }}>
                  <FontAwesomeIcon icon={faClipboardList} />
                  Recent Activities
                </h3>
                <div style={{
                  fontSize: "0.875rem",
                  color: "var(--primary-indigo)",
                  background: "rgba(46, 42, 116, 0.1)",
                  padding: "6px 12px",
                  borderRadius: "20px",
                }}>
                  {recentActivities.total_activities} activities
                </div>
              </div>
              
              <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                {recentActivities.activities.map((activity, index) => (
                  <div key={index} style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "15px",
                    borderBottom: index < recentActivities.activities.length - 1 ? "1px solid #f0f0f0" : "none",
                    background: activity.activity_type === 'Absent' ? "rgba(244, 67, 54, 0.05)" : "rgba(33, 150, 243, 0.05)",
                  }}>
                    <div style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      background: activity.activity_type === 'Absent' ? "#F44336" : "#2196F3",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      marginRight: "15px",
                    }}>
                      <FontAwesomeIcon 
                        icon={activity.activity_type === 'Absent' ? faExclamationTriangle : faChartBar} 
                        size="sm"
                      />
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: "500" }}>
                        {activity.student_name || activity.name} 
                        <span style={{ 
                          marginLeft: "10px", 
                          fontSize: "0.8rem", 
                          background: activity.activity_type === 'Absent' ? "#F44336" : "#2196F3",
                          color: "white",
                          padding: "2px 8px",
                          borderRadius: "10px"
                        }}>
                          {activity.activity_type}
                        </span>
                      </div>
                      <div style={{ fontSize: "0.875rem", color: "var(--text-secondary)", marginTop: "5px" }}>
                        {activity.class} • {activity.section || activity.class_section}
                      </div>
                    </div>
                    
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>
                        {formatTimeAgo(activity.created_at)}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginTop: "5px" }}>
                        {activity.exam_type || activity.day}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "24px",
              marginBottom: "48px",
            }}
          >
            <div
              style={{
                background: "var(--card-bg)",
                borderRadius: "var(--radius-xl)",
                padding: "24px",
                border: "2px solid var(--border-color)",
                boxShadow: "var(--shadow-md)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    background: "var(--primary-gradient)",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <FontAwesomeIcon icon={faUserShield} />
                </div>
                <h3 style={{ color: "var(--primary-indigo)", margin: 0 }}>
                  Security Dashboard
                </h3>
              </div>
              <p
                style={{
                  color: "var(--text-secondary)",
                  fontSize: "0.875rem",
                  lineHeight: "1.5",
                  marginBottom: "15px",
                }}
              >
                Monitor system security, access logs, and user activities in real-time.
              </p>
              {systemHealth && (
                <div style={{ 
                  background: "#f5f5f5", 
                  borderRadius: "10px", 
                  padding: "15px",
                  fontSize: "0.85rem" 
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                    <span>Database Status:</span>
                    <span style={{ color: systemHealth.system_status?.database === 'Connected' ? "#4CAF50" : "#F44336", fontWeight: "500" }}>
                      {systemHealth.system_status?.database || 'Unknown'}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                    <span>Response Time:</span>
                    <span>{systemHealth.system_status?.response_time || 'N/A'}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Last Backup:</span>
                    <span>{systemHealth.system_status?.last_backup ? new Date(systemHealth.system_status.last_backup).toLocaleDateString() : 'N/A'}</span>
                  </div>
                </div>
              )}
            </div>

            <div
              style={{
                background: "var(--card-bg)",
                borderRadius: "var(--radius-xl)",
                padding: "24px",
                border: "2px solid var(--border-color)",
                boxShadow: "var(--shadow-md)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    background:
                      "linear-gradient(135deg, #FFF200 0%, #FFD600 100%)",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--primary-indigo)",
                  }}
                >
                  <FontAwesomeIcon icon={faDatabase} />
                </div>
                <h3 style={{ color: "var(--primary-indigo)", margin: 0 }}>
                  Database Management
                </h3>
              </div>
              <p
                style={{
                  color: "var(--text-secondary)",
                  fontSize: "0.875rem",
                  lineHeight: "1.5",
                  marginBottom: "15px",
                }}
              >
                Backup, restore, and optimize your school database for maximum performance.
              </p>
              {systemHealth && systemHealth.table_stats && (
                <div style={{ 
                  background: "#f5f5f5", 
                  borderRadius: "10px", 
                  padding: "15px",
                  fontSize: "0.85rem" 
                }}>
                  <div style={{ fontWeight: "600", marginBottom: "10px" }}>Table Statistics:</div>
                  {Object.entries(systemHealth.table_stats).map(([table, stats]) => (
                    <div key={table} style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                      <span>{table}:</span>
                      <span>{stats.row_count} records</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              style={{
                background: "var(--card-bg)",
                borderRadius: "var(--radius-xl)",
                padding: "24px",
                border: "2px solid var(--border-color)",
                boxShadow: "var(--shadow-md)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    background:
                      "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <FontAwesomeIcon icon={faCog} />
                </div>
                <h3 style={{ color: "var(--primary-indigo)", margin: 0 }}>
                  System Settings
                </h3>
              </div>
              <p
                style={{
                  color: "var(--text-secondary)",
                  fontSize: "0.875rem",
                  lineHeight: "1.5",
                  marginBottom: "15px",
                }}
              >
                Configure system preferences, notifications, and integration settings.
              </p>
              <div style={{ 
                display: "flex", 
                flexDirection: "column",
                gap: "10px" 
              }}>
                <button 
                  style={{
                    padding: "10px 15px",
                    background: "var(--primary-indigo)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                  onClick={handleManualRefresh}
                  disabled={isRefreshing}
                >
                  <FontAwesomeIcon icon={faSyncAlt} style={{ marginRight: "8px" }} spin={isRefreshing} />
                  {isRefreshing ? "Refreshing..." : "Refresh Data"}
                </button>
                <div style={{ 
                  fontSize: "0.75rem", 
                  color: "var(--text-secondary)",
                  textAlign: "center",
                  marginTop: "5px"
                }}>
                  Last update: {lastRefreshTime ? formatLastRefresh() : new Date().toLocaleTimeString()}
                </div>
              </div>
            </div>
          </div>

          <footer className="dashboard-footer">
            <p>
              © 2024 <span className="footer-highlight">Designed By CK</span>{" "}
              With the Collaborations of{" "}
              <span className="footer-highlight">ORBIT Solutions</span>
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "24px",
                marginTop: "12px",
                fontSize: "0.75rem",
                color: "var(--text-secondary)",
                flexWrap: "wrap",
              }}
            >
              <span>v2.1.0</span>
              <span>•</span>
              <span>Last Updated: {new Date().toLocaleDateString()}</span>
              <span>•</span>
              <span>Students: {displayStats.totalStudents}</span>
              <span>•</span>
              <span>Teachers: {displayStats.totalTeachers}</span>
              <span>•</span>
              <span>Support: support@myschool.edu</span>
            </div>
          </footer>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
      `}</style>
    </>
  );
};

export default Dashboard;